import React, { useEffect, useState } from "react";
import AppContainer from "../../Components/Accounting/AppContainer";
import DataTable from "../../Components/Accounting/DataTable";
import Select2 from "../../Components/Accounting/Select2";
import ajaxAccounting from "../../util/accounting/remote/ajaxAccounting";
import functions from "../../util/accounting/functions";
import toast, { Toaster } from "react-hot-toast";
import AppModal from "../../Components/Accounting/AppModal";

const CYCLES = [
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "annually", label: "Annually" },
];

const subStatusBadge = (s) => {
  const map = { active: "success", paused: "warning", cancelled: "secondary", pending: "info", expired: "dark" };
  return <span className={`badge bg-${map[s] || "secondary"} text-capitalize`}>{s}</span>;
};

const SubscriptionBilling = () => {
  const [tab, setTab] = useState("subs");
  const [financialYears, setFinancialYears] = useState([]);
  const [selectedFY, setSelectedFY] = useState("");
  const [plans, setPlans] = useState([]);
  const [subs, setSubs] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [metrics, setMetrics] = useState({ mrr: null, churn: null });
  const [loading, setLoading] = useState(true);
  const [planModal, setPlanModal] = useState(false);
  const [subModal, setSubModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [planForm, setPlanForm] = useState({ code: "", name: "", price: "", billing_cycle: "monthly", account_id: "" });
  const [subForm, setSubForm] = useState({ contact_id: "", plan_id: "", start_date: new Date().toISOString().slice(0, 10) });

  useEffect(() => { init(); }, []);
  useEffect(() => { if (selectedFY) loadMetrics(selectedFY); }, [selectedFY]);

  const init = async () => {
    setLoading(true);
    const [fy, pl, sb, ac, ct] = await Promise.all([
      ajaxAccounting.listFinancialYears({}),
      ajaxAccounting.subListPlans({}),
      ajaxAccounting.subListSubscriptions({}),
      ajaxAccounting.listAccounts({ type: "revenue" }),
      ajaxAccounting.listContacts({ type: "customer" }),
    ]);
    if (fy.status === "OK") {
      setFinancialYears(fy.details || []);
      const cur = (fy.details || []).find((y) => y.is_current === 1 || y.is_current === "1");
      setSelectedFY(String(cur ? cur.id : fy.details?.[0]?.id || ""));
    }
    if (pl.status === "OK") setPlans(Array.isArray(pl.details) ? pl.details : pl.details?.list || []);
    if (sb.status === "OK") setSubs(Array.isArray(sb.details) ? sb.details : sb.details?.list || []);
    if (ac.status === "OK") setAccounts(ac.details || []);
    if (ct.status === "OK") setContacts(ct.details || []);
    setLoading(false);
  };

  const reloadLists = async () => {
    const [pl, sb] = await Promise.all([ajaxAccounting.subListPlans({}), ajaxAccounting.subListSubscriptions({})]);
    if (pl.status === "OK") setPlans(Array.isArray(pl.details) ? pl.details : pl.details?.list || []);
    if (sb.status === "OK") setSubs(Array.isArray(sb.details) ? sb.details : sb.details?.list || []);
  };

  const loadMetrics = async (fyId) => {
    const [mrr, churn] = await Promise.all([
      ajaxAccounting.subMrr({ financial_year_id: parseInt(fyId) }),
      ajaxAccounting.subChurn({ financial_year_id: parseInt(fyId) }),
    ]);
    setMetrics({ mrr: mrr.status === "OK" ? mrr.details : null, churn: churn.status === "OK" ? churn.details : null });
  };

  const createPlan = async () => {
    if (!planForm.code || !planForm.name || !planForm.price || !planForm.account_id) { toast.error("Code, name, price and revenue account are required."); return; }
    setSaving(true);
    const res = await ajaxAccounting.subCreatePlan({ ...planForm, price: parseFloat(planForm.price), account_id: parseInt(planForm.account_id) });
    if (res.status === "OK") { toast.success("Plan created!"); setPlanModal(false); setPlanForm({ code: "", name: "", price: "", billing_cycle: "monthly", account_id: "" }); reloadLists(); }
    else toast.error(res.message || "Failed to create plan");
    setSaving(false);
  };

  const createSub = async () => {
    if (!subForm.contact_id || !subForm.plan_id || !subForm.start_date) { toast.error("Customer, plan and start date are required."); return; }
    setSaving(true);
    const res = await ajaxAccounting.subscribe({ contact_id: parseInt(subForm.contact_id), plan_id: parseInt(subForm.plan_id), start_date: subForm.start_date, financial_year_id: parseInt(selectedFY) });
    if (res.status === "OK") { toast.success("Subscription created!"); setSubModal(false); setSubForm({ contact_id: "", plan_id: "", start_date: new Date().toISOString().slice(0, 10) }); reloadLists(); }
    else toast.error(res.message || "Failed to subscribe");
    setSaving(false);
  };

  const cancelSub = async (row) => {
    const reason = window.prompt("Reason for cancelling this subscription?");
    if (reason === null) return;
    if (!reason.trim()) { toast.error("A reason is required."); return; }
    const res = await ajaxAccounting.subCancel({ id: row.id, reason });
    if (res.status === "OK") { toast.success("Subscription cancelled."); reloadLists(); }
    else toast.error(res.message || "Cancel failed");
  };

  const metricVal = (obj, keys) => { if (!obj) return null; for (const k of keys) if (obj[k] !== undefined) return obj[k]; return null; };
  const mrrVal = metricVal(metrics.mrr, ["mrr", "total_mrr", "value"]);
  const churnVal = metricVal(metrics.churn, ["churn_rate", "churn", "rate", "value"]);

  const planCols = [
    { key: "code", label: "Code", render: (v) => <code className="fw-bold">{v}</code> },
    { key: "name", label: "Plan" },
    { key: "price", label: "Price", render: (v) => functions.formatCurrency(v) },
    { key: "billing_cycle", label: "Cycle", render: (v) => <span className="badge bg-light text-dark text-capitalize">{v}</span> },
    { key: "is_active", label: "Status", render: (v) => (v === 0 || v === "0") ? <span className="badge bg-secondary">Inactive</span> : <span className="badge bg-success">Active</span> },
  ];

  const subCols = [
    { key: "id", label: "#", render: (v) => <code>{v}</code> },
    { key: "contact_name", label: "Customer", render: (v, row) => v || row.contact || `Contact #${row.contact_id}` },
    { key: "plan_name", label: "Plan", render: (v, row) => v || row.plan || `Plan #${row.plan_id}` },
    { key: "amount", label: "Amount", render: (v, row) => functions.formatCurrency(v ?? row.price) },
    { key: "status", label: "Status", render: (v) => subStatusBadge(v) },
    { key: "next_billing_date", label: "Next Billing", render: (v, row) => { const d = v || row.next_bill_date; return d ? functions.formatDate(d) : "-"; } },
    {
      key: "id", label: "Action",
      render: (id, row) => (row.status === "active" || row.status === "paused")
        ? <button className="btn btn-sm btn-outline-danger" title="Cancel" onClick={() => cancelSub(row)}><i className="fa-solid fa-ban"></i></button>
        : null,
    },
  ];

  const fyOptions = financialYears.map((y) => ({ value: String(y.id), label: y.name + (y.is_current ? " (Current)" : "") }));

  return (
    <AppContainer
      title="Subscription Billing"
      previousPageTitle="Accounting"
      description="Manage recurring revenue: define subscription plans, subscribe customers, run billing, and track MRR and churn."
      actions={[
        { label: "New Plan", icon: "fa-solid fa-layer-group", className: "btn btn-outline-secondary", onClick: () => setPlanModal(true) },
        { label: "New Subscription", icon: "fa-solid fa-plus", className: "btn btn-primary", onClick: () => setSubModal(true) },
      ]}
    >
      <Toaster />
      <div className="row mb-3">
        <div className="col-lg-4">
          <div className="card custom-card p-3">
            <label className="form-label tx-12 fw-semibold mb-1">Financial Year</label>
            <Select2 options={fyOptions} value={selectedFY} onChange={setSelectedFY} placeholder="Select Financial Year" />
          </div>
        </div>
        <div className="col-lg-4">
          <div className="card custom-card" style={{ borderLeft: "4px solid #10b981", padding: "16px" }}>
            <div className="tx-11 tx-uppercase fw-semibold mb-1" style={{ color: "#10b981" }}>Monthly Recurring Revenue</div>
            <div className="fw-bold tx-20">{mrrVal !== null ? functions.formatCurrency(mrrVal) : "—"}</div>
          </div>
        </div>
        <div className="col-lg-4">
          <div className="card custom-card" style={{ borderLeft: "4px solid #ef4444", padding: "16px" }}>
            <div className="tx-11 tx-uppercase fw-semibold mb-1" style={{ color: "#ef4444" }}>Churn Rate</div>
            <div className="fw-bold tx-20">{churnVal !== null ? `${churnVal}${typeof churnVal === "number" ? "%" : ""}` : "—"}</div>
          </div>
        </div>
      </div>

      <ul className="nav nav-tabs mb-3">
        <li className="nav-item"><button className={`nav-link ${tab === "subs" ? "active" : ""}`} onClick={() => setTab("subs")}>Subscriptions ({subs.length})</button></li>
        <li className="nav-item"><button className={`nav-link ${tab === "plans" ? "active" : ""}`} onClick={() => setTab("plans")}>Plans ({plans.length})</button></li>
      </ul>

      <div className="card custom-card p-3">
        {tab === "subs"
          ? <DataTable columns={subCols} data={subs} loading={loading} pageSize={15} emptyMessage="No subscriptions yet." />
          : <DataTable columns={planCols} data={plans} loading={loading} pageSize={15} emptyMessage="No plans yet. Create one to start subscribing customers." />}
      </div>

      {/* New Plan modal */}
      {planModal && (
        <AppModal
          title="New Subscription Plan"
          size="lg"
          onClose={() => setPlanModal(false)}
          footer={
            <>
              <button className="btn btn-light" onClick={() => setPlanModal(false)}>Cancel</button><button className="btn btn-primary" onClick={createPlan} disabled={saving}>{saving ? "Saving..." : "Create Plan"}</button>
            </>
          }
        >
          <div className="row g-3">
            <div className="col-lg-4"><label className="form-label tx-12 fw-semibold">Code <span className="tx-danger">*</span></label><input className="form-control" value={planForm.code} onChange={(e) => setPlanForm({ ...planForm, code: e.target.value })} placeholder="e.g. PRO-M" /></div>
            <div className="col-lg-8"><label className="form-label tx-12 fw-semibold">Name <span className="tx-danger">*</span></label><input className="form-control" value={planForm.name} onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })} placeholder="e.g. Pro Monthly" /></div>
            <div className="col-lg-4"><label className="form-label tx-12 fw-semibold">Price <span className="tx-danger">*</span></label><input className="form-control" type="number" value={planForm.price} onChange={(e) => setPlanForm({ ...planForm, price: e.target.value })} /></div>
            <div className="col-lg-4"><label className="form-label tx-12 fw-semibold">Billing Cycle</label><Select2 options={CYCLES} value={planForm.billing_cycle} onChange={(v) => setPlanForm({ ...planForm, billing_cycle: v })} /></div>
            <div className="col-lg-4"><label className="form-label tx-12 fw-semibold">Revenue Account <span className="tx-danger">*</span></label><Select2 options={accounts.map((a) => ({ value: String(a.id), label: `${a.account_code} — ${a.name}` }))} value={planForm.account_id} onChange={(v) => setPlanForm({ ...planForm, account_id: v })} placeholder="Select account" /></div>
          </div>
        </AppModal>
      )}

      {/* New Subscription modal */}
      {subModal && (
        <AppModal
          title="New Subscription"
          onClose={() => setSubModal(false)}
          footer={
            <>
              <button className="btn btn-light" onClick={() => setSubModal(false)}>Cancel</button><button className="btn btn-primary" onClick={createSub} disabled={saving}>{saving ? "Saving..." : "Subscribe"}</button>
            </>
          }
        >
          <div className="row g-3">
            <div className="col-12"><label className="form-label tx-12 fw-semibold">Customer <span className="tx-danger">*</span></label><Select2 options={contacts.map((c) => ({ value: String(c.id), label: c.name }))} value={subForm.contact_id} onChange={(v) => setSubForm({ ...subForm, contact_id: v })} placeholder="Select customer" /></div>
            <div className="col-12"><label className="form-label tx-12 fw-semibold">Plan <span className="tx-danger">*</span></label><Select2 options={plans.map((p) => ({ value: String(p.id), label: `${p.name} — ${functions.formatCurrency(p.price)}/${p.billing_cycle}` }))} value={subForm.plan_id} onChange={(v) => setSubForm({ ...subForm, plan_id: v })} placeholder="Select plan" /></div>
            <div className="col-12"><label className="form-label tx-12 fw-semibold">Start Date <span className="tx-danger">*</span></label><input className="form-control" type="date" value={subForm.start_date} onChange={(e) => setSubForm({ ...subForm, start_date: e.target.value })} /></div>
          </div>
        </AppModal>
      )}
    </AppContainer>
  );
};

export default SubscriptionBilling;
